import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import BoltIcon from '@mui/icons-material/Bolt';
import SpeedIcon from '@mui/icons-material/Speed';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend, BarChart, Bar, Cell, ReferenceDot } from 'recharts';
import { useOptimization } from '../../context/OptimizationContext';
import { RESULT_ALGORITHM_COLORS, RESULT_ALGORITHM_NAMES, ResultAlgorithmKey } from '../../utils/algorithmRegistry';

const NAME_TO_KEY: Record<string, ResultAlgorithmKey> = {
  GA: 'ga',
  Cultural: 'culture',
  SFLA: 'frog',
  'SFLA-GA': 'sflaGa',
  SFLAGA: 'sflaga',
  PSO: 'pso',
  GAPSO: 'gapso',
};

const FALLBACK_COLORS = ['#5c6bc0', '#26c6da', '#ef6c00', '#8e24aa', '#2e7d32', '#ff7043', '#00897b'];

const flash = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(94, 234, 212, 0.7); transform: scale(1); }
  50% { box-shadow: 0 0 0 6px rgba(94, 234, 212, 0); transform: scale(1.03); }
  100% { box-shadow: 0 0 0 0 rgba(94, 234, 212, 0); transform: scale(1); }
`;

function extractAlgorithmNames(label: string): string[] {
  if (!label) return [];

  let normalized = label;
  normalized = normalized.replace(/^Complete Run:[^-]+-\s*/i, '');
  normalized = normalized.replace(/^Run\s+\d+\/\d+:\s*/i, '');
  if (normalized.includes(':')) {
    normalized = normalized.substring(normalized.lastIndexOf(':') + 1);
  }

  return normalized
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

const toGradientId = (name: string) => `grad-${name.replace(/[^a-z0-9]/gi, '').toLowerCase() || 'alg'}`;

const SimulationPanel: React.FC = () => {
  const {
    isRunning,
    progress,
    currentAlgorithm,
    currentIteration,
    totalIterations,
    currentRun,
    totalRuns,
    completedAlgorithms,
    liveConvergence,
  } = useOptimization();

  const activeAlgorithms = useMemo(() => new Set(extractAlgorithmNames(currentAlgorithm)), [currentAlgorithm]);

  const algorithmList = useMemo(() => {
    const list = new Set<string>();
    Object.keys(liveConvergence || {}).forEach((alg) => list.add(alg));
    completedAlgorithms.forEach((alg) => list.add(alg));
    activeAlgorithms.forEach((alg) => list.add(alg));
    return Array.from(list);
  }, [activeAlgorithms, completedAlgorithms, liveConvergence]);

  const mergedData = useMemo(() => {
    const iterations = new Set<number>();
    const fitnessByAlgorithm = new Map<string, Map<number, number>>();

    Object.entries(liveConvergence).forEach(([alg, points]) => {
      const series = new Map<number, number>();
      (points || []).forEach(({ iteration, fitness }) => {
        if (!Number.isFinite(iteration)) {
          return;
        }
        iterations.add(iteration);
        series.set(iteration, fitness);
      });
      fitnessByAlgorithm.set(alg, series);
    });

    return Array.from(iterations)
      .sort((a, b) => a - b)
      .map((iteration) => {
      const row: Record<string, number> & { iteration: number } = { iteration };
      fitnessByAlgorithm.forEach((series, alg) => {
        const fitness = series.get(iteration);
        if (fitness !== undefined) {
          row[alg] = fitness;
        }
      });
      return row;
    });
  }, [liveConvergence]);

  const latestPoints = useMemo(() => {
    const entries: { alg: string; iteration: number; value: number }[] = [];
    Object.entries(liveConvergence).forEach(([alg, points]) => {
      if (!points || points.length === 0) return;
      const last = points[points.length - 1];
      entries.push({ alg, iteration: last.iteration, value: last.fitness });
    });
    return entries;
  }, [liveConvergence]);

  const hasLiveData = mergedData.length > 0;
  const iterationPercent = totalIterations > 0 ? Math.min(100, (currentIteration / totalIterations) * 100) : 0;

  const getColor = (name: string, index: number) => {
    const key = NAME_TO_KEY[name];
    return (key && RESULT_ALGORITHM_COLORS[key]) || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  };

  return (
    <Box sx={{ color: 'white' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            <Paper
              elevation={4}
              sx={{
                p: 2.5,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'white',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Overall Progress
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {Math.round(progress)}%
                  </Typography>
                </Stack>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress
                    variant="determinate"
                    value={Math.max(progress, 6)}
                    size={82}
                    thickness={5}
                    sx={{ color: '#5eead4' }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="subtitle2" component="div" color="white">
                      {currentRun > 0 ? `${currentRun}/${totalRuns}` : 'Run'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {totalRuns > 1 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Statistical Run
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(currentRun / totalRuns) * 100}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      '& .MuiLinearProgress-bar': { backgroundColor: '#93c5fd' },
                    }}
                  />
                </Box>
              )}

              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Iteration Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <SpeedIcon fontSize="small" sx={{ color: '#5eead4' }} />
                  <Typography variant="body2" fontWeight={600}>
                    {currentIteration} / {totalIterations || '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', color: 'rgba(255,255,255,0.7)' }}>
                    {Math.round(iterationPercent)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={iterationPercent}
                  sx={{
                    mt: 1,
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    '& .MuiLinearProgress-bar': { backgroundColor: '#5eead4' },
                  }}
                />
              </Box>
            </Paper>

            <Paper
              elevation={3}
              sx={{
                p: 2.5,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'white',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BoltIcon fontSize="small" sx={{ color: '#fcd34d' }} />
                <Typography variant="subtitle1" fontWeight={700}>
                  Algorithm Stream
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {algorithmList.length === 0 && (
                  <Chip label="Waiting for activity" size="small" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.2)' }} variant="outlined" />
                )}
                {algorithmList.map((alg, idx) => {
                  const isDone = completedAlgorithms.has(alg);
                  const isActive = activeAlgorithms.has(alg) && isRunning;
                  const color = getColor(alg, idx);

                  return (
                    <Chip
                      key={alg}
                      label={alg}
                      size="small"
                      variant={isDone ? 'filled' : 'outlined'}
                      sx={{
                        color: 'white',
                        backgroundColor: isDone ? color : 'transparent',
                        borderColor: isActive ? color : 'rgba(255,255,255,0.3)',
                        fontWeight: isActive ? 700 : 500,
                        boxShadow: isActive ? `0 0 0 1px ${color}` : 'none',
                        animation: isActive ? `${flash} 1.6s ease-in-out infinite` : 'none',
                      }}
                      icon={isActive ? <AutoAwesomeIcon fontSize="small" sx={{ color }} /> : undefined}
                    />
                  );
                })}
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper
            elevation={4}
            sx={{
              p: 2.5,
              height: '100%',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: 'white',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Live Convergence
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Streaming the recorded convergence points for the active run
                </Typography>
              </Box>
              <Chip
                label={isRunning ? 'Streaming' : 'Paused'}
                color={isRunning ? 'success' : 'default'}
                size="small"
                sx={{ color: isRunning ? 'white' : 'rgba(255,255,255,0.8)' }}
              />
            </Box>

            <Box sx={{ height: 320 }}>
              {hasLiveData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={mergedData} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
                    <defs>
                      {Object.keys(liveConvergence).map((alg, idx) => (
                        <linearGradient key={alg} id={toGradientId(alg)} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={getColor(alg, idx)} stopOpacity={0.78} />
                          <stop offset="95%" stopColor={getColor(alg, idx)} stopOpacity={0.08} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="iteration" stroke="rgba(255,255,255,0.7)" tick={{ fontSize: 12 }} />
                    <YAxis stroke="rgba(255,255,255,0.7)" tick={{ fontSize: 12 }} domain={['auto', 'auto']} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#111827', border: '1px solid rgba(255,255,255,0.1)' }}
                      labelStyle={{ color: '#e5e7eb' }}
                    />
                    <Legend wrapperStyle={{ color: 'white', fontSize: 12 }} />
                    {Object.keys(liveConvergence).map((alg, idx) => (
                      <Area
                        key={alg}
                        type="monotone"
                        dataKey={alg}
                        stroke={getColor(alg, idx)}
                        fill={`url(#${toGradientId(alg)})`}
                        strokeWidth={2.2}
                        dot={false}
                        connectNulls
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        isAnimationActive={false}
                      />
                    ))}
                    {latestPoints.map((p, idx) => (
                      <ReferenceDot
                        key={`${p.alg}-current`}
                        x={p.iteration}
                        y={p.value}
                        r={4.5}
                        fill="#ffffff"
                        stroke={getColor(p.alg, idx)}
                        strokeWidth={2}
                        label={{
                          value: `${p.alg}: ${p.value.toFixed(4)}`,
                          position: 'top',
                          fill: getColor(p.alg, idx),
                          fontSize: 11,
                        }}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.7)', flexDirection: 'column', gap: 1 }}>
                  <CircularProgress size={32} sx={{ color: '#5eead4' }} />
                  <Typography variant="body2">Waiting for the first iterations...</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const EnergySimulationPanel: React.FC = () => {
  const { results, isRunning, completedAlgorithms, currentAlgorithm } = useOptimization();

  const algorithms = useMemo(() => {
    const names: string[] = [];
    const includeBaseline = Boolean(results?.configuration?.includeSflaGaBaseline ?? results?.sflaGa);
    const keys: ResultAlgorithmKey[] = includeBaseline
      ? ['ga', 'culture', 'frog', 'sflaGa', 'sflaga', 'pso', 'gapso']
      : ['ga', 'culture', 'frog', 'sflaga', 'pso', 'gapso'];
    keys.forEach((k) => names.push(RESULT_ALGORITHM_NAMES[k] || RESULT_ALGORITHM_NAMES[k as ResultAlgorithmKey] || k.toUpperCase()));
    return names;
  }, [results]);

  const data = useMemo(() => {
    return algorithms.map((alg) => {
      const key = NAME_TO_KEY[alg];
      const energy = key && results && (results as any)[key]?.energy;
      return { name: alg, energy: energy ?? null };
    });
  }, [algorithms, results]);

  const activeAlgorithms = useMemo(() => new Set(extractAlgorithmNames(currentAlgorithm)), [currentAlgorithm]);

  const hasValues = data.some((d) => d.energy !== null && d.energy !== undefined);

  const getColor = (name: string, index: number) => {
    const key = NAME_TO_KEY[name];
    return (key && RESULT_ALGORITHM_COLORS[key]) || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  };

  return (
    <Box sx={{ color: 'white' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: 2.5,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: 'white',
            }}
          >
            <Stack spacing={1.25}>
              <Typography variant="h6" fontWeight={700}>
                Energy Snapshot
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Updates as each algorithm finishes. Active algorithms flash until they complete.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {algorithms.map((alg, idx) => {
                  const isDone = completedAlgorithms.has(alg) && Boolean(data[idx]?.energy !== null);
                  const isActive = activeAlgorithms.has(alg) && isRunning;
                  const color = getColor(alg, idx);
                  return (
                    <Chip
                      key={alg}
                      label={alg}
                      size="small"
                      variant={isDone ? 'filled' : 'outlined'}
                      sx={{
                        color: 'white',
                        backgroundColor: isDone ? color : 'transparent',
                        borderColor: isActive ? color : 'rgba(255,255,255,0.3)',
                        fontWeight: isActive ? 700 : 500,
                        animation: isActive ? `${flash} 1.6s ease-in-out infinite` : 'none',
                        boxShadow: isActive ? `0 0 0 1px ${color}` : 'none',
                      }}
                      icon={isActive ? <AutoAwesomeIcon fontSize="small" sx={{ color }} /> : undefined}
                    />
                  );
                })}
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper
            elevation={4}
            sx={{
              p: 2.5,
              height: '100%',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: 'white',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Energy by Algorithm (lower is better)
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Fills as algorithms complete during the run.
                </Typography>
              </Box>
              <Chip
                label={isRunning ? 'Streaming' : hasValues ? 'Complete' : 'Waiting'}
                color={isRunning ? 'success' : hasValues ? 'default' : 'warning'}
                size="small"
                sx={{ color: 'white' }}
              />
            </Box>

            <Box sx={{ height: 260 }}>
              {hasValues ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="name" stroke="rgba(255,255,255,0.7)" tick={{ fontSize: 12 }} />
                    <YAxis stroke="rgba(255,255,255,0.7)" tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }}
                      itemStyle={{ color: '#ffffff' }}
                      formatter={(v: any) => (v === null ? 'pending' : `${Number(v).toFixed(4)} J`)}
                    />
                    <Bar dataKey="energy" radius={6}>
                      {data.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={getColor(entry.name, index)}
                          stroke="rgba(255,255,255,0.8)"
                          strokeWidth={0}
                          cursor="pointer"
                          onMouseEnter={(e: any) => {
                            if (e && e.target) e.target.setAttribute('fill', '#ffffff');
                          }}
                          onMouseLeave={(e: any) => {
                            if (e && e.target) e.target.setAttribute('fill', getColor(entry.name, index));
                          }}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.7)', flexDirection: 'column', gap: 1 }}>
                  <CircularProgress size={28} sx={{ color: '#fcd34d' }} />
                  <Typography variant="body2">Waiting for energy results...</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SimulationPanel;
export { EnergySimulationPanel };
